import { useState, useEffect } from 'react';
import { DashboardPage } from '../../components/PremiumDesign';
import { EmailChatArea } from '../components/EmailChatArea';
import {
  useInfiniteGmailConversations,
  useCustomGmailMessages,
  useSendGmailMessage,
  useGmailSyncStatus,
  useSyncGmail,
  useGmailCategories,
} from '../hooks/useGmailChat';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import { useDelayedSkeleton } from '@/common/hooks';
import { formatMediaUrl } from '../utils/groupMessages';

function formatTime(dateString) {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
}

function formatMessageTime(dateString) {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'hh:mm a');
  } catch {
    return dateString;
  }
}

export function EmailInboxPage() {
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('primary');
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeLabel, setActiveLabel] = useState(null);

  // Get company ID from local/session storage
  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore
    }
  }

  // Reset active conversation on folder / category / label change
  useEffect(() => {
    setActiveConversation(null);
  }, [activeCategory, activeFolder, activeLabel]);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const queryParams = {
    search: debouncedSearchQuery || undefined,
    category:
      !activeLabel && activeFolder === 'inbox'
        ? activeCategory || undefined
        : undefined,
    folder:
      activeFolder !== 'inbox' || activeLabel
        ? activeFolder || undefined
        : undefined,
    label: activeLabel || undefined,
  };

  const gmailConversations = useInfiniteGmailConversations(
    currentCompanyId,
    queryParams,
    true,
  );

  const gmailSyncStatusQuery = useGmailSyncStatus(currentCompanyId, true);
  const gmailCategoriesQuery = useGmailCategories(currentCompanyId, true);
  const startGmailSyncMutation = useSyncGmail();

  const handleStartSync = (mode = 'all') => {
    startGmailSyncMutation.mutate({ companyId: currentCompanyId, mode });
  };

  const {
    data: conversationsData,
    isLoading: isApiLoadingConversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchConversations,
  } = gmailConversations;

  const isLoadingConversations = useDelayedSkeleton(
    isApiLoadingConversations,
    0,
  );

  const gmailMessages = useCustomGmailMessages(
    currentCompanyId,
    activeConversation,
    true,
  );

  const {
    messages: rawMessages,
    isLoadingInitial: isApiLoadingMessages,
    fetchNewerMessages: refetchMessages,
  } = gmailMessages;

  const isLoadingMessages = isApiLoadingMessages;

  // Group messages by date for UI
  useEffect(() => {
    if (!rawMessages || rawMessages.length === 0) {
      setMessages((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const sortedMessages = [...rawMessages].sort((a, b) => {
      const tA = new Date(a.timestamp || a.date || 0).getTime();
      const tB = new Date(b.timestamp || b.date || 0).getTime();
      return tA - tB;
    });

    const grouped = {};
    sortedMessages.forEach((msg) => {
      let dateKey = 'Today';
      const ts = msg.timestamp || msg.date;
      if (ts) {
        try {
          const d = new Date(ts);
          if (!isNaN(d.getTime())) {
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            if (d.toDateString() === today.toDateString()) {
              dateKey = 'Today';
            } else if (d.toDateString() === yesterday.toDateString()) {
              dateKey = 'Yesterday';
            } else {
              dateKey = format(d, 'MMMM d, yyyy');
            }
          }
        } catch {
          dateKey = 'Today';
        }
      }

      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(msg);
    });

    const uniqueDatesAsc = [
      ...new Set(
        sortedMessages.map((msg) => {
          const ts = msg.timestamp || msg.date;
          if (!ts) return 'Today';
          try {
            const d = new Date(ts);
            if (isNaN(d.getTime())) return 'Today';
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            if (d.toDateString() === today.toDateString()) return 'Today';
            if (d.toDateString() === yesterday.toDateString())
              return 'Yesterday';
            return format(d, 'MMMM d, yyyy');
          } catch {
            return 'Today';
          }
        }),
      ),
    ];

    const finalMappedGroups = uniqueDatesAsc.map((dateKey) => {
      const msgs = grouped[dateKey].map((msg) => {
        const firstAttachment = msg.attachments?.[0];
        const rawUrl =
          firstAttachment?.file_url ||
          firstAttachment?.file ||
          msg.file_url ||
          msg.file ||
          msg.mediaUrl;
        const mediaUrl = formatMediaUrl(rawUrl);

        return {
          id: msg.id?.toString(),
          content: msg.content || msg.body || msg.text || '',
          direction:
            msg.direction ||
            (msg.sender_type === 'client' ? 'inbound' : 'outbound'),
          time: formatMessageTime(msg.timestamp || msg.date),
          status: msg.status || (msg.is_read ? 'read' : 'delivered'),
          messageType: msg.message_type,
          mediaUrl: mediaUrl,
          mimeType: firstAttachment?.mime_type || null,
          fileName:
            firstAttachment?.file_name ||
            firstAttachment?.filename ||
            firstAttachment?.name,
          fileSize:
            firstAttachment?.file_size ||
            firstAttachment?.filesize ||
            firstAttachment?.size,
          timestamp: msg.timestamp,
          isDeleted: msg.is_deleted,
          reaction: msg.reaction || [],
          template: msg.template || null,
          attachments: msg.attachments || [],
          is_html:
            msg.is_html ||
            Boolean(
              (msg.content || msg.body) &&
              (/<[a-z][\s\S]*>/i.test(msg.content || msg.body) ||
                (msg.content || msg.body).includes('<p>') ||
                (msg.content || msg.body).includes('<p ') ||
                (msg.content || msg.body).includes('<br') ||
                (msg.content || msg.body).includes('<div>') ||
                (msg.content || msg.body).includes('<div ') ||
                (msg.content || msg.body).includes('</span>') ||
                (msg.content || msg.body).includes('class=')),
            ),
          from_name: msg.from_name || msg.from,
          from_email: msg.from_email,
          to_email: msg.to_email || msg.to,
          subject: msg.subject,
        };
      });

      return { date: dateKey, messages: msgs };
    });

    setMessages(finalMappedGroups);
  }, [rawMessages]);

  const conversationsList = (conversationsData?.pages || [])
    .flatMap((page) => page?.results || [])
    .map((conv) => {
      const name =
        conv.name?.trim() ||
        conv.customer_name?.trim() ||
        conv.from_name?.trim() ||
        'Unknown';
      const lastMessageText =
        conv.last_message_preview ||
        conv.last_message?.body ||
        (typeof conv.last_message === 'string' ? conv.last_message : null) ||
        'Tap to view messages...';

      const isUnread =
        typeof conv.is_unread === 'boolean'
          ? conv.is_unread
          : typeof conv.is_read === 'boolean'
            ? !conv.is_read
            : typeof conv.unread === 'boolean'
              ? conv.unread
              : Boolean(conv.unread_count && conv.unread_count > 0);

      return {
        ...conv,
        id: conv.id?.toString(),
        contactName: name,
        contactPhone: conv.from_email || '',
        lastMessage: lastMessageText,
        time: formatTime(
          conv.last_message_at ||
            conv.last_inbound_message_at ||
            conv.last_message?.timestamp ||
            conv.timestamp ||
            conv.date ||
            conv.created_at,
        ),
        is_unread: isUnread,
        unreadCount: isUnread ? conv.unread_count || 1 : 0,
        unread_count: isUnread ? conv.unread_count || 1 : 0,
        avatarUrl: conv.profile_picture_url || null,
        raw: conv,
      };
    });

  const sendGmailMessage = useSendGmailMessage();

  const handleSendMessage = async (
    text,
    file,
    customSubject,
    targetTo,
    cc,
    bcc,
  ) => {
    if (!text?.trim() && !file) return;
    if (!activeConversation || !currentCompanyId) return;

    const newMessageId = Date.now().toString();
    const newMessage = {
      id: newMessageId,
      content: text || '',
      direction: 'outbound',
      time: 'Just now',
      status: 'sending',
      is_html: true,
    };

    // Optimistic UI update
    setMessages((prevGroups) => {
      const todayStr = 'Today';
      if (prevGroups.length === 0) {
        return [{ date: todayStr, messages: [newMessage] }];
      }
      const lastGroup = prevGroups[prevGroups.length - 1];
      if (
        lastGroup.date === todayStr ||
        lastGroup.date === format(new Date(), 'MMMM d, yyyy')
      ) {
        return [
          ...prevGroups.slice(0, -1),
          { ...lastGroup, messages: [...lastGroup.messages, newMessage] },
        ];
      }
      return [...prevGroups, { date: todayStr, messages: [newMessage] }];
    });

    const isForward = customSubject?.toLowerCase().startsWith('fwd:');
    const payload = {
      to:
        targetTo ||
        activeConversation.from_email ||
        activeConversation.raw?.from_email ||
        activeConversation.contactPhone ||
        activeConversation.name,
      cc: cc || undefined,
      bcc: bcc || undefined,
      subject:
        customSubject ||
        (activeConversation.subject
          ? `Re: ${activeConversation.subject}`
          : 'Reply from Aexon'),
      body: text,
      thread_id: isForward
        ? undefined
        : activeConversation.thread_id || activeConversation.raw?.thread_id,
    };

    sendGmailMessage.mutate(
      {
        companyId: currentCompanyId,
        payload: payload,
      },
      {
        onSuccess: () => {
          refetchMessages();
        },
        onError: () => {
          toast.error('Failed to send email');
          setMessages((prevGroups) => {
            return prevGroups.map((group) => ({
              ...group,
              messages: group.messages.map((m) =>
                m.id === newMessageId ? { ...m, status: 'failed' } : m,
              ),
            }));
          });
        },
      },
    );
  };

  return (
    <DashboardPage
      noScroll={true}
      contentClassName="h-full min-h-0 flex flex-col p-4"
    >
      <div className="flex-1 min-h-0 w-full h-full overflow-hidden">
        <EmailChatArea
          conversations={conversationsList}
          isLoadingConversations={isLoadingConversations}
          activeConversation={activeConversation}
          companyId={currentCompanyId}
          onSelectConversation={(conv) => setActiveConversation(conv)}
          messages={messages}
          isLoadingMessages={isLoadingMessages}
          onSendMessage={handleSendMessage}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onRefresh={() => {
            refetchConversations({
              refetchPage: (page, index) => index === 0,
            });
            if (activeConversation) {
              refetchMessages();
            }
          }}
          onNextPage={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          totalApiCount={conversationsData?.pages?.[0]?.count}
          syncInfo={
            gmailSyncStatusQuery.data?.sync || gmailConversations.syncInfo
          }
          onStartSync={handleStartSync}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          activeFolder={activeFolder}
          onFolderChange={setActiveFolder}
          activeLabel={activeLabel}
          onLabelChange={setActiveLabel}
          categories={gmailCategoriesQuery.data || []}
        />
      </div>
    </DashboardPage>
  );
}
